import { ClinicAuthError } from "@auth/clinicAuthClient";
import { faPause, faPhone } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CallerPreview } from "@notifications/CallerPreview";
import { CallSuggestionsPanel } from "@notifications/CallSuggestionsPanel";
import {
  useIncomingCallStore,
  type IncomingCall,
  type LiveCallStatus,
} from "@notifications/incomingCallStore";
import {
  PULSE_DOT,
  settledOutcome,
  SoftphonePanel,
  useTalkTime,
} from "@notifications/SoftphonePanel";
import { useCallBubbleHosted } from "@notifications/useCornerCall";
import { useDragOffset } from "@notifications/useDragOffset";
import { useHeldFor } from "@notifications/useHeldFor";
import { getInstallationId } from "@phone/browserInstallation";
import { contextCallerName, useCallContext } from "@phone/callContextClient";
import { OutcomeChip } from "@phone/OutcomeChip";
import {
  cancelPhoneCall,
  disclosureNoteFor,
  type HoldConflict,
  holdConflictOf,
  holdPhoneCall,
  isCancellable,
  isHoldable,
  resumePhoneCall,
  voicemailPhoneCall,
} from "@phone/phoneCallsClient";
import {
  formatPhoneNumber,
  inboundRingsComputers,
} from "@phone/phoneSetupClient";
import {
  answerIncoming,
  cancelPickup,
  declineIncoming,
  expectPickup,
  type IncomingRing,
  type SoftphoneOwnership,
  type SoftphoneStatus,
  useSoftphoneStore,
} from "@phone/softphone";
import { useOutboundLines } from "@phone/useOutboundLines";
import {
  Button,
  cn,
  PaperPanel,
  StatusMark,
  StickyNote,
  Swipe,
} from "@scribenote/brian-design-system";
import { useOpenCalls } from "@shell/navigation";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";

/** Dismiss throws away the card, never the call. `key={callUuid}` replays the entrance and live-region announcement. */
export const IncomingCallCards = () => {
  const calls = useIncomingCallStore((s) => s.calls);
  const placedCallUuid = useSoftphoneStore((s) => s.placedCallUuid);
  const answeredCallUuid = useSoftphoneStore((s) => s.answeredCallUuid);
  const openCalls = useOpenCalls();
  const bubbleHosted = useCallBubbleHosted();
  // The whole pile drags: the "+N more" sheet is positioned against it.
  const { dragging, moved, dragProps } = useDragOffset();

  if (calls.length === 0) return null;

  // The headset call renders last, outside the pile: the bottom-anchored column
  // grows upward, so only the last child keeps its place when a slip rings.
  const headset =
    calls.find((c) => c.callUuid === answeredCallUuid) ??
    calls.find(
      (c) => c.origin === "staff_outbound" && c.callUuid === placedCallUuid,
    ) ??
    null;
  const piled = headset
    ? calls.filter((c) => c.callUuid !== headset.callUuid)
    : calls;
  // An answered inbound call's controls live in the call bubble where one is mounted.
  const panel =
    headset && !(bubbleHosted && headset.origin === "inbound") ? headset : null;

  const top = piled.length > 0 ? piled[piled.length - 1] : null;
  const waiting = Math.max(piled.length - 1, 0);

  return (
    <div className="flex flex-col items-end gap-3">
      {top && (
        <div
          data-testid="incoming-call-pile"
          {...dragProps}
          className={cn(
            "relative",
            waiting > 0 && "mb-7",
            dragging ? "cursor-grabbing select-none" : "cursor-grab",
            (dragging || moved) && "z-20",
          )}
        >
          {waiting > 0 && (
            <button
              type="button"
              data-testid="incoming-call-overflow"
              onClick={openCalls}
              className={cn(
                "absolute inset-0 flex translate-x-2 translate-y-7 cursor-pointer",
                "items-end justify-end rounded-paper border border-rule bg-card px-3 pb-1.5 shadow-lift",
                "text-annotation-caps text-annotation",
                "transition duration-150 ease-paper hover:text-ink-body",
                "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
              )}
            >
              +{waiting} more
            </button>
          )}
          <IncomingCallCard key={top.callUuid} call={top} />
        </div>
      )}
      {panel && <SoftphonePanel key={panel.callUuid} call={panel} />}
    </div>
  );
};

/** "bridged" borrows `handed_off`'s wording: the destination may have been a mailbox (phoneCallOutcome.ts). */
const STATUS_EYEBROW: Record<LiveCallStatus, string> = {
  ringing: "Incoming call",
  disclosing: "Recording notice",
  handling: "Brian is on the call",
  dialing: "Ringing the clinic",
  out_dialing_staff: "Brian is calling your line",
  out_awaiting_confirmation: "Pick up and press 1 to connect",
  out_dialing_recipient: "Calling",
  out_disclosing: "Connecting…",
  out_connecting: "Connecting…",
  bridged: "Connected to clinic",
};

const OUTBOUND_BRIDGED_EYEBROW = "On the call with";
const RINGING_HERE_EYEBROW = "Ringing this computer";

/** Inbound statuses during which an `inbound_ring` that includes computers may still ring this one. */
const RING_EXPECTED: ReadonlySet<LiveCallStatus> = new Set<LiveCallStatus>([
  "ringing",
  "disclosing",
  "dialing",
]);

/** "ready" with no ring: brian's step excluded this browser (late or occupied), or the greeting is still playing. */
const silentHereEyebrow = (
  ownership: SoftphoneOwnership,
  status: SoftphoneStatus,
): string => {
  if (ownership === "non-owner") return "Ringing in another tab";
  if (status === "error") return "Headset not connected";
  if (status === "ready") return "Ringing the clinic phones";
  return "Connecting this computer's headset";
};

const lastDigits = (number: string): string =>
  number.replace(/\D/g, "").slice(-10);

/** The sole unheld inbound call, else the sole held one: a pick-up ring may carry no uuid or number. */
const soleRingCandidate = (calls: readonly IncomingCall[]): string | null => {
  const inbound = calls.filter((c) => c.origin === "inbound");
  const live = inbound.filter((c) => c.holdState === "none");
  const candidates = live.length > 0 ? live : inbound;
  return candidates.length === 1 ? candidates[0].callUuid : null;
};

/** Last ten digits: number formats differ between provider and brian. */
const ringIsFor = (
  ring: IncomingRing,
  call: IncomingCall,
  ringCandidate: string | null,
): boolean => {
  if (ring.callUuid !== null) return ring.callUuid === call.callUuid;
  if (ring.remoteNumber && call.fromNumber) {
    const remote = lastDigits(ring.remoteNumber);
    const from = lastDigits(call.fromNumber);
    if (remote !== "" && from !== "") return remote === from;
  }
  return ringCandidate === call.callUuid;
};

/** The identity block is RUM-hidden (README); the testid never interpolates the call uuid. */
const IncomingCallCard = ({ call }: { call: IncomingCall }) => {
  const context = useCallContext(call);
  const close = useIncomingCallStore((s) => s.close);
  const ringCandidate = useIncomingCallStore((s) => soleRingCandidate(s.calls));
  const ring = useSoftphoneStore((s) => s.incomingRing);
  const ownership = useSoftphoneStore((s) => s.ownership);
  const softphoneStatus = useSoftphoneStore((s) => s.status);
  const offered = useOutboundLines();
  const inboundComputers =
    offered.status === "ready" && inboundRingsComputers(offered.inboundRing);
  const openCalls = useOpenCalls();
  const outbound = call.origin === "staff_outbound";
  // Outbound only: an inbound `started_at` is the caller's leg reaching the provider (brian `modules/phone/transitions.py`).
  const duration = useTalkTime(call, true);
  const ringingHere =
    !outbound && ring !== null && ringIsFor(ring, call, ringCandidate);
  const silentHere =
    inboundComputers &&
    !outbound &&
    !ringingHere &&
    RING_EXPECTED.has(call.status);

  // Outbound: the frame's label is the clinic's own caller ID, so it is never the title.
  const recipient = call.recipientE164
    ? formatPhoneNumber(call.recipientE164)
    : null;
  const recipientName = call.clientName ?? call.callerName;
  const fromNumber = call.fromNumber
    ? formatPhoneNumber(call.fromNumber)
    : null;
  const title = outbound
    ? (recipientName ?? recipient ?? "Placing a call")
    : (contextCallerName(call, context.data) ??
      fromNumber ??
      "Incoming caller");
  const number = outbound
    ? recipientName && recipient
      ? recipient
      : null
    : fromNumber && fromNumber !== title && call.fromNumber !== title
      ? fromNumber
      : null;
  const pets = call.clientPatients?.length
    ? call.clientPatients.join(", ")
    : null;

  const view = () => {
    close(call.callUuid);
    openCalls();
  };

  const settled = outbound && call.settled ? call.settled : null;
  const outcome = settledOutcome(settled);
  const bridged = call.status === "bridged";
  const disclosure = disclosureNoteFor(bridged, call.disclosureState);
  const remindDisclosure =
    outbound &&
    !outcome &&
    offered.status === "ready" &&
    !offered.outboundDisclosureSpoken;
  // A ring for a held call with no pick-up expectation here gets the normal Answer/Decline.
  const onHold = !outbound && call.holdState !== "none" && !ringingHere;
  const heldFor = useHeldFor(onHold ? call.heldAt : null);

  return (
    <PaperPanel
      variant="card"
      lifted
      role="status"
      data-testid="incoming-call"
      data-hold-state={call.holdState}
      className={cn(
        "relative z-10 flex max-h-[calc(100dvh-var(--call-popup-space,7rem))] max-w-[calc(100vw-2rem)] flex-col p-3 [animation:bds-slip-in_220ms_var(--ease-paper)_backwards] motion-reduce:animate-none",
        outbound
          ? "w-80"
          : context.data?.client
            ? "w-[26rem]"
            : context.expanded
              ? "w-[30rem]"
              : "w-80",
      )}
    >
      <div className="shrink-0">
        {outcome ? (
          <OutcomeChip label={outcome.label} tone={outcome.tone} />
        ) : onHold ? (
          <>
            {call.holdState === "held" ? (
              <Swipe
                tone="flag"
                className="text-annotation-caps"
                icon={<FontAwesomeIcon icon={faPause} className="text-[9px]" />}
                data-testid="incoming-call-on-hold"
              >
                On hold
                {heldFor && (
                  <>
                    {" · "}
                    <span className="font-mono tabular-nums tracking-normal">
                      {heldFor}
                    </span>
                  </>
                )}
              </Swipe>
            ) : (
              <Swipe
                tone="working"
                className="text-annotation-caps"
                icon={<span className={PULSE_DOT} />}
                data-testid="incoming-call-resuming"
              >
                Picking up…
              </Swipe>
            )}
            <p className="mt-1 text-fine-print text-annotation">
              {call.holdState === "held"
                ? "Hearing hold music"
                : "Connecting them to the desk that picked up"}
            </p>
          </>
        ) : silentHere ? (
          <StatusMark data-testid="incoming-call-silent-here">
            {silentHereEyebrow(ownership, softphoneStatus)}
          </StatusMark>
        ) : (
          <Swipe
            tone="working"
            className="text-annotation-caps"
            icon={<span className={PULSE_DOT} />}
          >
            {disclosure.playing
              ? "Playing recording notice…"
              : ringingHere
                ? RINGING_HERE_EYEBROW
                : outbound && bridged
                  ? OUTBOUND_BRIDGED_EYEBROW
                  : STATUS_EYEBROW[call.status]}
          </Swipe>
        )}

        {!outcome && disclosure.note && (
          <p
            className="mt-1 text-fine-print text-annotation"
            data-testid="incoming-call-disclosure-note"
          >
            {disclosure.note}
          </p>
        )}

        {remindDisclosure && (
          <StickyNote
            className="mt-2 min-h-0 p-2.5 pt-3"
            data-testid="incoming-call-disclosure-reminder"
          >
            <p className="text-sm text-ink-body">
              Brian isn't playing the recording notice. Tell them this call is
              recorded.
            </p>
          </StickyNote>
        )}

        <div className="mt-2" data-dd-privacy="hidden">
          <p className="truncate font-medium text-ink">{title}</p>
          {call.unreachable && !outcome && (
            <p className="mt-0.5 text-sm text-annotation">
              Can't reach Brian to update this call.
            </p>
          )}
          {outbound && pets && (
            <p className="mt-0.5 truncate text-sm text-ink-body">{pets}</p>
          )}
          {number && (
            <p className="mt-1 font-mono text-fine-print text-annotation">
              {number}
            </p>
          )}
          {duration && (
            <p
              className="mt-1 font-mono text-fine-print text-annotation"
              data-testid="incoming-call-duration"
            >
              {duration}
            </p>
          )}
          {outcome?.helper && (
            <p
              className="mt-1 text-fine-print text-annotation"
              data-testid="incoming-call-outcome-helper"
            >
              {outcome.helper}
            </p>
          )}
        </div>
      </div>
      {!outbound && (
        <div
          data-call-context-scroll
          className="min-h-0 overflow-y-auto overscroll-contain"
        >
          <CallerPreview context={context} fromNumber={call.fromNumber} />
          <CallSuggestionsPanel callUuid={call.callUuid} />
        </div>
      )}
      <div className="shrink-0">
        {onHold ? (
          // Keyed so the `resuming` grace clock and any stale error reset per hold phase.
          <HeldCallActions key={call.holdState} call={call} />
        ) : (
          <div className="mt-3 flex items-center justify-end gap-1">
            {ringingHere ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={declineIncoming}
                  data-testid="incoming-call-decline"
                >
                  Decline
                </Button>
                <Button
                  variant="accent"
                  size="sm"
                  onClick={() => answerIncoming(call.callUuid)}
                  data-testid="incoming-call-answer"
                >
                  Answer
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => close(call.callUuid)}
                >
                  Dismiss
                </Button>
                {outbound && !settled && isCancellable(call.status) ? (
                  <CancelCallButton
                    key={call.status}
                    callUuid={call.callUuid}
                  />
                ) : isHoldable(call) ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="incoming-call-view"
                      onClick={view}
                    >
                      View call
                    </Button>
                    <HoldCallButton callUuid={call.callUuid} />
                  </>
                ) : (
                  <Button
                    variant="accent"
                    size="sm"
                    data-testid="incoming-call-view"
                    onClick={view}
                  >
                    View call
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </PaperPanel>
  );
};

/** Offered even while this headset is busy: brian never rings an occupied browser for the second caller. */
const HoldCallButton = ({ callUuid }: { callUuid: string }) => {
  const hold = useMutation({
    mutationFn: () => holdPhoneCall(callUuid),
    meta: { handled: true },
  });
  return (
    <span className="inline-flex flex-col items-end gap-1">
      <Button
        variant="accent"
        size="sm"
        busy={hold.isPending}
        onClick={() => hold.mutate()}
        data-testid="incoming-call-hold"
      >
        <FontAwesomeIcon icon={faPause} aria-hidden="true" />
        Hold
      </Button>
      {hold.isError && (
        <span
          role="status"
          className="text-fine-print text-ink-stopped"
          data-testid="incoming-call-hold-error"
        >
          {holdErrorNote(hold.error, "Couldn't put them on hold. Try again.")}
        </span>
      )}
    </span>
  );
};

/** `browser_busy` is also pre-empted client-side, but another tab or desk phone can be the busy one. */
const HOLD_CONFLICT_NOTES: Record<HoldConflict, string> = {
  already_held: "Someone already put this caller on hold.",
  not_holdable: "Too late — the call moved on.",
  browser_unavailable: "Turn on this computer's headset to pick up.",
  browser_busy: "Hang up your current call to pick up.",
  not_held: "This caller isn't on hold any more.",
};

/** Longer than brian's `BROWSER_RING_TIMEOUT_SECONDS`, shorter than softphone.ts `PICKUP_WINDOW_MS`. */
const RESUMING_GRACE_MS = 25_000;

const holdErrorNote = (error: unknown, fallback: string): string => {
  const conflict = holdConflictOf(error);
  return conflict ? HOLD_CONFLICT_NOTES[conflict] : fallback;
};

/** Pick up expects the ring before the POST: brian can dial this leg before the response lands. */
const HeldCallActions = ({ call }: { call: IncomingCall }) => {
  const busyHere = useSoftphoneStore((s) => s.call !== null);
  const resuming = call.holdState === "resuming";
  // `resuming` outlives an unanswered browser ring; past the grace, brian accepts a fresh pick-up.
  const [graceOver, setGraceOver] = useState(false);
  useEffect(() => {
    if (!resuming) return;
    const timer = window.setTimeout(
      () => setGraceOver(true),
      RESUMING_GRACE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [resuming]);
  const waiting = resuming && !graceOver;
  const pickUp = useMutation({
    mutationFn: async () => {
      expectPickup(call.callUuid);
      try {
        return await resumePhoneCall(call.callUuid, getInstallationId());
      } catch (error) {
        cancelPickup(call.callUuid);
        throw error;
      }
    },
    meta: { handled: true },
  });
  const toVoicemail = useMutation({
    mutationFn: () => {
      // A stale ring for the abandoned pick-up must be offered, not auto-answered.
      cancelPickup(call.callUuid);
      return voicemailPhoneCall(call.callUuid);
    },
    meta: { handled: true },
  });
  // Only a call gone from the store drops the expectation; other unmounts are phase changes or a covering slip.
  useEffect(
    () => () => {
      const gone = !useIncomingCallStore
        .getState()
        .calls.some((c) => c.callUuid === call.callUuid);
      if (gone) cancelPickup(call.callUuid);
    },
    [call.callUuid],
  );
  const pending = pickUp.isPending || toVoicemail.isPending;
  const error = pickUp.isError
    ? holdErrorNote(pickUp.error, "Couldn't pick up. Try again.")
    : toVoicemail.isError
      ? holdErrorNote(
          toVoicemail.error,
          "Couldn't send them to voicemail. Try again.",
        )
      : null;

  return (
    <div className="mt-3 flex flex-col items-end gap-1">
      <div className="flex w-full items-center justify-between gap-2">
        <Button
          variant="outline"
          size="sm"
          busy={toVoicemail.isPending}
          disabled={pending || waiting}
          onClick={() => toVoicemail.mutate()}
          data-testid="incoming-call-voicemail"
        >
          Send to voicemail
        </Button>
        <Button
          variant="accent"
          size="sm"
          busy={pickUp.isPending || waiting}
          disabled={busyHere || pending}
          onClick={() => pickUp.mutate()}
          data-testid="incoming-call-pickup"
        >
          <FontAwesomeIcon icon={faPhone} aria-hidden="true" />
          Pick up
        </Button>
      </div>
      {busyHere && !error && (
        <span
          className="text-right text-fine-print text-annotation"
          data-testid="incoming-call-pickup-busy"
        >
          Hang up your current call to pick up
        </span>
      )}
      {resuming && graceOver && !error && (
        <span
          className="text-right text-fine-print text-annotation"
          data-testid="incoming-call-pickup-stalled"
        >
          Taking a while — pick up here, or send them to voicemail.
        </span>
      )}
      {error && (
        <span
          role="status"
          className="text-right text-fine-print text-ink-stopped"
          data-testid="incoming-call-hold-error"
        >
          {error}
        </span>
      )}
    </div>
  );
};

/** Idempotent on brian's side; a 409 means staff already pressed 1 and the recipient is ringing. */
const CancelCallButton = ({ callUuid }: { callUuid: string }) => {
  const [tooLate, setTooLate] = useState(false);
  const cancel = useMutation({
    mutationFn: () => cancelPhoneCall(callUuid),
    // A 409 is a state, not a failure; the write floor's message would misname it.
    meta: { handled: true },
    onError: (error) => {
      if (error instanceof ClinicAuthError && error.status === 409)
        setTooLate(true);
    },
  });
  if (tooLate)
    return (
      <span
        className="text-fine-print text-annotation"
        data-testid="incoming-call-cancel-late"
      >
        Too late to cancel — the call is connecting.
      </span>
    );
  return (
    <span className="inline-flex flex-col items-end gap-1">
      <Button
        variant="outline"
        size="sm"
        busy={cancel.isPending}
        onClick={() => cancel.mutate()}
        data-testid="incoming-call-cancel"
      >
        Cancel
      </Button>
      {cancel.isError && (
        <span
          role="status"
          className="text-fine-print text-ink-stopped"
          data-testid="incoming-call-cancel-error"
        >
          Couldn't cancel. Try again.
        </span>
      )}
    </span>
  );
};
